/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction, KeyValuePair, Undefined } from '../../../helpers/jsHelpers/types';
import type { ParentalControlItemState } from '../../../helpers/parentalControl/constsAndTypes';
import type { ChannelMap } from '../../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_V8_AUTHENT_REALM } from '../../../libs/netgemLibrary/v8/types/Realm';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { NETGEM_API_V8_RIGHTS } from '../../../libs/netgemLibrary/v8/types/Rights';
import type { NETGEM_API_VIEWINGHISTORY } from '../../../libs/netgemLibrary/v8/types/ViewingHistory';
import type { CombinedReducers } from '../../../redux/reducers';
import type { Dispatch } from '../../../redux/types/types';
import type { CARD_DATA_MODAL_TYPE } from '../../modal/cardModal/CardModalConstsAndTypes';
import type { MESSAGE_MODAL_TYPE } from '../../modal/messageModal/MessageModal';
import type { PIN_DATA_MODAL_TYPE } from '../../modal/pinModal/PinModalConstsAndTypes';

import { APPLICATION_ID } from '../../../helpers/applicationCustomization/types';
import AccurateTimestamp from '../../../helpers/dateTime/AccurateTimestamp';
import { formatDate, formatExpirationDate } from '../../../helpers/dateTime/Format';
import { showDebug } from '../../../helpers/debug/debug';
import { LoadableStatus } from '../../../helpers/loadable/loadable';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { getEpisodeAndSeriesRecordStatus, getScheduledRecordingIdFromRecordingId } from '../../../helpers/npvr/recording';
import { ParentalControlLockReason, ParentalControlLockState } from '../../../helpers/parentalControl/constsAndTypes';
import { showUnavailableContentModal } from '../../../helpers/parentalControl/helpers';
import ParentalControlManager from '../../../helpers/parentalControl/parentalControlManager';
import { isPlaybackGranted } from '../../../helpers/rights/playback';
import { ExtendedItemType } from '../../../helpers/ui/item/types';
import { BroadcastStatus, WebAppHelpersLocationStatus, getBroadcastStatus, getLocationStatus } from '../../../helpers/ui/location/Format';
import { formatSeasonEpisodeNbr, getStartAndEndTimes, getSynopsis, isEpisodeIndexInTitle, renderProgramDetails } from '../../../helpers/ui/metadata/Format';
import { PROGRAM_INFO } from '../../../helpers/ui/metadata/Types';
import { getCardUrlDefinitionForItem } from '../../../helpers/ui/section/tile';
import { getProgress } from '../../../helpers/viewingHistory/ViewingHistory';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import getTranslatedText from '../../../libs/netgemLibrary/v8/helpers/Lang';
import { ItemContent, ItemLocationKind, type NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { type NETGEM_RECORDINGS_MAP, type NETGEM_SCHEDULED_RECORDINGS_MAP, RecordingOutcome, ScheduledRecordingsKind } from '../../../libs/netgemLibrary/v8/types/Npvr';
import { type NetgemApiV8WidgetItemClick, TileConfigTileType } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import { FEATURE_SUBSCRIPTION } from '../../../redux/appConf/constants';
import { RegistrationType } from '../../../redux/appRegistration/types/types';
import { hideModal, showMessageModal, showPinModal } from '../../../redux/modal/actions';
import { getImageUrl } from '../../../redux/netgemApi/actions/v8/metadataImage';
import sendV8MetadataLocationRequest from '../../../redux/netgemApi/actions/v8/metadataLocation';
import { sendV8RecordingsMetadataRequest } from '../../../redux/netgemApi/actions/v8/recordings';
import AccessibleButton from '../../buttons/AccessibleButton';
import { PinModalKind } from '../../modal/pinModal/PinModalConstsAndTypes';
import { PictoArrowDown, PictoClockEmpty, PictoFailure, PictoLock, PictoRecord, PictoWarning } from '../../pictos/Element';
import RecordButton, { ContextKind, DisplayMode } from '../../recordButton/RecordButton';
import StatusPicto from '../../statusPicto/StatusPicto';
import './TVEpisode.css';
import WatchingStatus from '../../watchingStatus/WatchingStatus';

// Time display and broadcast status are refreshed every second (in ms)
const REFRESH_TIMEOUT = 1_000;

const IMAGE_WIDTH = 284;
const IMAGE_HEIGHT = 160;

type ReduxTVEpisodeDispatchToPropsType = {
  +localGetImageUrl: (assetId: string, width: number, height: number, signal?: AbortSignal) => Promise<string>,
  +localHideModal: BasicFunction,
  +localSendV8MetadataLocationRequest: (assetId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>,
  +localSendV8RecordingsMetadataRequest: (recordId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>,
  +localShowMessageModal: (messageData: MESSAGE_MODAL_TYPE) => void,
  +localShowPinModal: (data: PIN_DATA_MODAL_TYPE) => void,
};

type ReduxTVEpisodeReducerStateType = {
  +channels: ChannelMap,
  +commercialOffers: ?KeyValuePair<Array<string>>,
  +defaultOnItemClick: ?NetgemApiV8WidgetItemClick,
  +defaultRights: ?NETGEM_API_V8_RIGHTS,
  +isDebugModeEnabled: boolean,
  +isRegisteredAsGuest: boolean,
  +isSubscriptionFeatureEnabled: boolean,
  +npvrRecordingsFuture: NETGEM_RECORDINGS_MAP,
  +npvrRecordingsList: NETGEM_RECORDINGS_MAP,
  +npvrScheduledRecordingsList: NETGEM_SCHEDULED_RECORDINGS_MAP,
  +userRights: Array<string> | null,
  +viewingHistory: NETGEM_API_VIEWINGHISTORY,
  +viewingHistoryStatus: LoadableStatus,
};

type DefaultProps = {
  +focusedLocationId?: string,
  +onItemClick?: NetgemApiV8WidgetItemClick,
};

type TVEpisodePropType = {
  ...DefaultProps,
  +cardData: CARD_DATA_MODAL_TYPE,
  +isExpanded: boolean,
  +index: number,
  +item: NETGEM_API_V8_FEED_ITEM,
  +onToggleExpandedState: (index: number) => void,
  +programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  +seriesMetadata: NETGEM_API_V8_METADATA_SERIES,
  +tileType: TileConfigTileType,
};

type CompleteTVEpisodePropType = {
  ...TVEpisodePropType,
  ...ReduxTVEpisodeReducerStateType,
  ...ReduxTVEpisodeDispatchToPropsType,
};

type TVEpisodeStateType = {
  authority?: NETGEM_API_V8_AUTHENT_REALM,
  broadcastStatus: BroadcastStatus,
  canBePlayed: boolean,
  imageUrl: ?string,
  isLiveRecording: boolean,
  now: number,
  parentalControlState: ParentalControlItemState,
  previewCatchupScheduledEventId: string | null,
  tvLocationMetadata: ?NETGEM_API_V8_METADATA_SCHEDULE,
};

const InitialState = Object.freeze({
  authority: undefined,
  broadcastStatus: BroadcastStatus.Unknown,
  canBePlayed: false,
  imageUrl: null,
  isLiveRecording: false,
  parentalControlState: {
    hasPinCode: false,
    lockReason: undefined,
    lockState: ParentalControlLockState.NotInitialized,
  } as ParentalControlItemState,
  previewCatchupScheduledEventId: null,
  tvLocationMetadata: null,
});

class TVEpisode extends React.PureComponent<CompleteTVEpisodePropType, TVEpisodeStateType> {
  abortController: AbortController;
  broadcastStatusRefreshTimer: TimeoutID | null;
  failedRecordingId: ?string;
  isSvodDisplayedAsCatchup: boolean;
  recordOutcome: ?RecordingOutcome;
  synopsis: ?string;
  timeRefreshTimer: TimeoutID | null;
  warningScheduledEventId: ?string;
  warningScheduledRecordingId: ?string;

  static defaultProps: DefaultProps = {
    focusedLocationId: undefined,
    onItemClick: undefined,
  };

  constructor(props: CompleteTVEpisodePropType) {
    super(props);

    const {
      item: { locType },
    } = props;

    this.abortController = new AbortController();
    this.broadcastStatusRefreshTimer = null;
    this.failedRecordingId = null;
    this.isSvodDisplayedAsCatchup = locType === ItemLocationKind.SVOD;
    this.recordOutcome = null;
    this.synopsis = null;
    this.timeRefreshTimer = null;
    this.warningScheduledEventId = null;
    this.warningScheduledRecordingId = null;

    this.state = {
      ...InitialState,
      now: AccurateTimestamp.nowInSeconds(),
    };
  }

  componentDidMount() {
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);

    this.update();
  }

  componentDidUpdate(prevProps: CompleteTVEpisodePropType, prevState: TVEpisodeStateType) {
    const { npvrRecordingsList, npvrScheduledRecordingsList } = this.props;
    const { npvrRecordingsList: prevNpvrRecordingsList, npvrScheduledRecordingsList: prevNpvrScheduledRecordingsList } = prevProps;
    const { broadcastStatus, previewCatchupScheduledEventId } = this.state;
    const { broadcastStatus: prevBroadcastStatus, previewCatchupScheduledEventId: prevPreviewCatchupScheduledEventId } = prevState;

    if (broadcastStatus !== prevBroadcastStatus) {
      this.loadTVLocationMetadata();

      if (broadcastStatus === BroadcastStatus.Live) {
        // Item became live
        this.startTimeRefreshTimer();
      } else {
        // Item is not live anymore
        this.resetTimeRefreshTimer();
      }

      this.updateRecordStatus();
    }

    if (npvrRecordingsList !== prevNpvrRecordingsList || npvrScheduledRecordingsList !== prevNpvrScheduledRecordingsList || previewCatchupScheduledEventId !== prevPreviewCatchupScheduledEventId) {
      this.updateRecordStatus();
    }
  }

  componentWillUnmount() {
    const { abortController } = this;

    abortController.abort('Component TVEpisode will unmount');

    Messenger.off(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.off(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);

    this.resetTimeRefreshTimer();
    this.resetBroadcastStatusRefreshTimer();
  }

  updateLockState: () => void = () => {
    const { item } = this.props;

    this.setState({ parentalControlState: ParentalControlManager.getItemStateFromItem(item) });
  };

  showDebugInfo: () => void = () => {
    const { props, state } = this;

    showDebug('SeriesSection TVEpisode', {
      instance: this,
      instanceFields: ['failedRecordingId', 'isSvodDisplayedAsCatchup', 'recordOutcome', 'synopsis', 'warningScheduledRecordingId', 'warningScheduledEventId'],
      misc: { watchingStatus: this.getWatchingStatus() },
      props,
      propsFields: ['focusedLocationId', 'index', 'isExpanded', 'item', 'onItemClick', 'programMetadata', 'seriesMetadata'],
      state,
      stateFields: ['authority', 'broadcastStatus', 'canBePlayed', 'imageUrl', 'isLiveRecording', 'now', 'parentalControlState', 'previewCatchupScheduledEventId', 'tvLocationMetadata'],
    });
  };

  startTimeRefreshTimer: () => void = () => {
    this.resetTimeRefreshTimer();
    this.timeRefreshTimer = setTimeout(this.updateTime, REFRESH_TIMEOUT);
  };

  resetTimeRefreshTimer: () => void = () => {
    if (this.timeRefreshTimer) {
      clearTimeout(this.timeRefreshTimer);
      this.timeRefreshTimer = null;
    }
  };

  startBroadcastStatusRefreshTimer: () => void = () => {
    const { broadcastStatus } = this.state;

    this.resetBroadcastStatusRefreshTimer();

    if (broadcastStatus === BroadcastStatus.Past) {
      // A past item will never change, no need to check again
      return;
    }

    this.broadcastStatusRefreshTimer = setTimeout(this.updateBroadcastStatus, REFRESH_TIMEOUT);
  };

  resetBroadcastStatusRefreshTimer: () => void = () => {
    if (this.broadcastStatusRefreshTimer) {
      clearTimeout(this.broadcastStatusRefreshTimer);
      this.broadcastStatusRefreshTimer = null;
    }
  };

  updateTime: () => void = () => {
    this.setState({ now: AccurateTimestamp.nowInSeconds() }, this.startTimeRefreshTimer);
  };

  updateBroadcastStatus: () => void = () => {
    const { item } = this.props;
    const { isSvodDisplayedAsCatchup } = this;

    // Item is an SVOD but is displayed as a catchup (e.g. Okoo)
    this.setState({ broadcastStatus: isSvodDisplayedAsCatchup ? BroadcastStatus.Past : getBroadcastStatus(item) }, this.startBroadcastStatusRefreshTimer);
  };

  update: () => void = () => {
    const { programMetadata } = this.props;

    this.synopsis = getSynopsis(programMetadata, Localizer.language);

    this.setState({
      ...InitialState,
      authority: programMetadata?.authority,
    });

    this.updateLockState();

    this.loadImage();
    this.updateBroadcastStatus();
  };

  isPlayable = (): boolean => {
    const {
      item: { locType },
    } = this.props;
    const { broadcastStatus } = this.state;
    const { isSvodDisplayedAsCatchup, recordOutcome } = this;

    return (
      (broadcastStatus === BroadcastStatus.Past && locType === ItemLocationKind.Catchup) ||
      broadcastStatus === BroadcastStatus.Live ||
      broadcastStatus === BroadcastStatus.Preview ||
      (locType === ItemLocationKind.Recording && recordOutcome === RecordingOutcome.Recorded) ||
      isSvodDisplayedAsCatchup
    );
  };

  updateRecordStatus: () => void = () => {
    const {
      item,
      item: { selectedProgramId },
      npvrRecordingsFuture,
      npvrRecordingsList,
      npvrScheduledRecordingsList,
      seriesMetadata,
    } = this.props;
    const { broadcastStatus, previewCatchupScheduledEventId } = this.state;

    const { failedRecordingId, hasRecording, recordOutcome, warningScheduledEventId, warningScheduledRecordingId } = getEpisodeAndSeriesRecordStatus(
      selectedProgramId,
      seriesMetadata?.id,
      item,
      npvrRecordingsList,
      npvrRecordingsFuture,
      npvrScheduledRecordingsList,
      previewCatchupScheduledEventId,
    );

    const scheduledRecordingId = warningScheduledRecordingId || (failedRecordingId ? getScheduledRecordingIdFromRecordingId(failedRecordingId, npvrScheduledRecordingsList) : null);

    this.recordOutcome = recordOutcome;
    this.failedRecordingId = failedRecordingId;
    this.warningScheduledRecordingId = scheduledRecordingId;
    this.warningScheduledEventId = warningScheduledEventId;

    this.setState({
      canBePlayed: this.isPlayable(),
      isLiveRecording: hasRecording && broadcastStatus === BroadcastStatus.Live,
    });
  };

  loadImage: () => void = () => {
    const {
      item: { selectedProgramId },
      localGetImageUrl,
    } = this.props;
    const {
      abortController: { signal },
    } = this;

    localGetImageUrl(selectedProgramId, IMAGE_WIDTH, IMAGE_HEIGHT, signal)
      .then((imageUrl: string) => {
        signal.throwIfAborted();

        this.setState({ imageUrl });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  loadTVLocationMetadata: () => void = () => {
    const {
      item,
      item: {
        locType,
        selectedLocation: { id },
      },
      localSendV8MetadataLocationRequest,
      localSendV8RecordingsMetadataRequest,
    } = this.props;
    const { authority, broadcastStatus } = this.state;
    const {
      abortController: { signal },
    } = this;

    if (!id) {
      return;
    }

    const locationStatus = getLocationStatus(item, authority);
    const request =
      locationStatus === WebAppHelpersLocationStatus.Recording || (locationStatus === WebAppHelpersLocationStatus.Live && locType === ItemLocationKind.Recording)
        ? localSendV8RecordingsMetadataRequest
        : localSendV8MetadataLocationRequest;

    request(id, signal)
      .then((requestResponse: NETGEM_API_V8_REQUEST_RESPONSE) => {
        signal.throwIfAborted();

        const {
          result,
          result: {
            location: { target },
          },
        } = requestResponse;
        this.setState({
          previewCatchupScheduledEventId: broadcastStatus === BroadcastStatus.Preview ? target : null,
          tvLocationMetadata: result,
        });

        const { focusedLocationId, index, isExpanded, onToggleExpandedState } = this.props;

        /*
         * In the case of a live episode being recorded, the focused location is a scheduled event, while the episode
         * location is a recording, so we have to look at the target (GM-2513)
         */
        if (!isExpanded && result.location.target === focusedLocationId) {
          onToggleExpandedState(index);
        }
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  playInternal = (isUnlockedAdultContent: boolean): void => {
    const { cardData, channels, commercialOffers, defaultRights, index, isRegisteredAsGuest, isSubscriptionFeatureEnabled, item, localHideModal, programMetadata, seriesMetadata, userRights } =
      this.props;
    const { authority, broadcastStatus, previewCatchupScheduledEventId, tvLocationMetadata } = this.state;

    // TODO: change this when Continuous Watching works for other apps
    if (broadcastStatus === BroadcastStatus.Past && process.env.REACT_APP_ID === APPLICATION_ID.FranceChannel) {
      // Tell card to play episode, so that continuous watching works
      Messenger.emit(MessengerEvents.CONTINUOUS_WATCHING_PLAY_FROM_EPISODE, index);
      return;
    }

    // Otherwise, legacy behavior

    const localCardData: CARD_DATA_MODAL_TYPE = {
      ...cardData,
      item,
      programMetadata,
      seriesMetadata,
    };

    if (
      !isPlaybackGranted(
        isRegisteredAsGuest,
        isSubscriptionFeatureEnabled,
        localCardData,
        authority,
        ItemContent.TV,
        tvLocationMetadata?.location,
        channels,
        commercialOffers,
        defaultRights,
        userRights,
      )
    ) {
      // Playback denied: log-in or subscribe page has been requested
      localHideModal();
      return;
    }

    Messenger.emit(MessengerEvents.OPEN_PLAYER, {
      authority,
      isUnlockedAdultContent,
      item,
      openFromCard: true,
      previewCatchupScheduledEventId,
      programMetadata,
      seriesMetadata,
      type: ExtendedItemType.TV,
    });
  };

  handlePlayButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>): void => {
    const { index, isExpanded, item, localShowMessageModal, localShowPinModal, onToggleExpandedState, programMetadata } = this.props;
    const {
      parentalControlState: { hasPinCode, lockReason, lockState },
    } = this.state;

    event.preventDefault();
    event.stopPropagation();

    if (!programMetadata || lockState === ParentalControlLockState.NotInitialized) {
      return;
    }

    if (!isExpanded) {
      // Expand item before play, so it will automatically reopen when player exits
      onToggleExpandedState(index);
    }

    if (!hasPinCode && lockReason === ParentalControlLockReason.AdultContent) {
      // Content is locked and cannot be unlocked
      showUnavailableContentModal(localShowMessageModal);
      return;
    }

    if (lockState === ParentalControlLockState.Locked) {
      // Episode is locked
      if (hasPinCode) {
        // Ask PIN code
        Messenger.once(MessengerEvents.MODAL_CLOSED, this.parentalControlContentUnlockClosedCallback);
        localShowPinModal({ item, kind: lockReason === ParentalControlLockReason.AdultContent ? PinModalKind.UnlockAdultContent : PinModalKind.UnlockContent });
      } else {
        // PIN code creation
        localShowPinModal({ kind: PinModalKind.CreateMaster });
      }
      return;
    }

    // Episode is not locked
    this.playInternal(false);
  };

  parentalControlContentUnlockClosedCallback = (isValidPin: boolean): void => {
    if (isValidPin) {
      this.playInternal(true);
    }
  };

  handleOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    const { index, isDebugModeEnabled, onToggleExpandedState } = this.props;
    const { altKey, ctrlKey } = event;

    if (isDebugModeEnabled && (ctrlKey || altKey)) {
      this.showDebugInfo();
    } else {
      onToggleExpandedState(index);
    }
  };

  getWatchingStatus = (): number | null => {
    const { item, programMetadata, seriesMetadata, viewingHistory, viewingHistoryStatus } = this.props;

    if (viewingHistoryStatus !== LoadableStatus.Loaded) {
      return null;
    }

    return getProgress(viewingHistory, item, programMetadata, seriesMetadata, false, null);
  };

  handleErrorPictoOnClick: () => void = () => {
    const { failedRecordingId, warningScheduledEventId, warningScheduledRecordingId } = this;

    Messenger.emit(MessengerEvents.OPEN_NPVR_MANAGEMENT_SCREEN, warningScheduledRecordingId, failedRecordingId, warningScheduledEventId);
  };

  renderRecordErrorPicto = (recordOutcome: ?RecordingOutcome): React.Node => {
    const { broadcastStatus } = this.state;

    if (!recordOutcome) {
      return null;
    }

    let description: ?string = null;
    if (recordOutcome === RecordingOutcome.OutOfQuota) {
      description = Localizer.localize('modal.card.status_picto.warning_out_of_quota');
    } else if (recordOutcome === RecordingOutcome.ExceededConcurrency) {
      description = Localizer.localize('modal.card.status_picto.warning_exceeded_concurrency');
    } else if (recordOutcome === RecordingOutcome.ServerError) {
      description = Localizer.localize('modal.card.status_picto.warning_server');
    } else {
      return null;
    }

    let pictoElement = null;
    if (broadcastStatus === BroadcastStatus.Past || broadcastStatus === BroadcastStatus.Live) {
      description = Localizer.localize('modal.card.status_picto.failure');
      pictoElement = <PictoFailure className='alertPicto failure' />;
    } else {
      pictoElement = <PictoWarning className='alertPicto warning' />;
    }

    return (
      <div className='recordError' onClick={this.handleErrorPictoOnClick}>
        {pictoElement}
        <div className='description'>{description}</div>
      </div>
    );
  };

  renderTitle = (): React.Node => {
    const { index, item, programMetadata } = this.props;
    const { broadcastStatus, now } = this.state;

    if (!programMetadata) {
      return null;
    }

    const { titles } = programMetadata;
    const { endTime, startTime } = getStartAndEndTimes(item);
    const date = formatDate(startTime, endTime, now);

    let title = getTranslatedText(titles, Localizer.language);
    const episodeIndex = isEpisodeIndexInTitle(title) ? null : formatSeasonEpisodeNbr(programMetadata);

    if (episodeIndex) {
      title = `${episodeIndex}${title ? ' : ' : ''}${title}`;
    }

    const isFuture = broadcastStatus === BroadcastStatus.Future;
    const progress = broadcastStatus === BroadcastStatus.Past || broadcastStatus === BroadcastStatus.Preview ? this.getWatchingStatus() : null;

    return (
      <div className='dateAndTitle' id={`${isFuture ? 'future' : 'available'}-episode-title-${index}`}>
        <div className='date'>{date}</div>
        <div className='title'>{title}</div>
        {progress !== null && progress > 0 ? <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.play_progress', { progress })}</span> : null}
      </div>
    );
  };

  renderActions = (): React.Node => {
    const {
      defaultOnItemClick,
      item,
      item: { locType },
      onItemClick,
      programMetadata,
      seriesMetadata,
      tileType,
    } = this.props;
    const { broadcastStatus, canBePlayed, previewCatchupScheduledEventId, tvLocationMetadata } = this.state;

    const progress: number | null = broadcastStatus === BroadcastStatus.Past || broadcastStatus === BroadcastStatus.Preview ? this.getWatchingStatus() : null;

    const watchingStatus = canBePlayed ? <WatchingStatus allowZeroProgress={false} onClick={this.handlePlayButtonOnClick} progress={progress} /> : null;

    let cardDataUrlDefinition: Undefined<NETGEM_API_V8_URL_DEFINITION> = undefined;
    if (tvLocationMetadata) {
      // In case of TV item, add the program/series card URL definition (if found) so that the episodes and the related items will be displayed on the card
      const urlDefinition = getCardUrlDefinitionForItem(item, seriesMetadata, onItemClick, defaultOnItemClick);
      if (urlDefinition) {
        cardDataUrlDefinition = urlDefinition;
      }
    }

    return (
      <>
        <RecordButton
          broadcastStatus={broadcastStatus}
          cardDataUrlDefinition={cardDataUrlDefinition}
          context={ContextKind.TVEpisode}
          displayMode={DisplayMode.Picto}
          item={item}
          key='recordButton'
          kind={locType === ItemLocationKind.Catchup ? ScheduledRecordingsKind.KeepFromReplay : ScheduledRecordingsKind.Single}
          location={tvLocationMetadata?.location ?? null}
          previewCatchupScheduledEventId={previewCatchupScheduledEventId}
          programMetadata={programMetadata}
          seriesMetadata={seriesMetadata}
          tileType={tileType}
        />
        {watchingStatus}
      </>
    );
  };

  renderTime = (): React.Node => {
    const {
      item,
      item: { locType, selectedLocation },
    } = this.props;
    const { availabilityEndDate } = selectedLocation || {};

    if ((locType === ItemLocationKind.Catchup || locType === ItemLocationKind.Recording) && availabilityEndDate) {
      // Expiration date
      return (
        <div className='date expiration' key='expirationDate'>
          {formatExpirationDate(availabilityEndDate)}
        </div>
      );
    }

    // Broadcast date and time
    const { endTime, startTime } = getStartAndEndTimes(item);

    return (
      <div className='date broadcast' key='broadcastDate'>
        {formatDate(startTime, endTime, AccurateTimestamp.nowInSeconds())}
      </div>
    );
  };

  renderLockOverlay = (): React.Node => {
    const {
      cardData: { isUnlockedAdultContent },
    } = this.props;
    const {
      parentalControlState: { lockState },
    } = this.state;

    if (lockState !== ParentalControlLockState.Locked || isUnlockedAdultContent === true) {
      return null;
    }

    return (
      <div className='lockOverlay'>
        <PictoLock aria-hidden />
      </div>
    );
  };

  renderHeader = (): React.Node => {
    const { index, isExpanded } = this.props;
    const { broadcastStatus } = this.state;

    const isFuture = broadcastStatus === BroadcastStatus.Future;

    return (
      <AccessibleButton
        aria-controls={`${isFuture ? 'future' : 'available'}-episode-panel-${index}`}
        aria-expanded={isExpanded}
        aria-labelledby={`${isFuture ? 'future' : 'available'}-episode-title-${index}`}
        className={clsx('header', isFuture && 'future')}
        id={`${isFuture ? 'future' : 'available'}-episode-header-${index}`}
        onClick={this.handleOnClick}
      >
        {isFuture ? <PictoClockEmpty aria-hidden className='clock' /> : null}
        {this.renderTitle()}
        <div className='actions'>
          {this.renderActions()}
          <PictoArrowDown aria-hidden className='arrow' forceHoverEffect />
        </div>
      </AccessibleButton>
    );
  };

  renderContent = (): React.Node => {
    const { index, isExpanded, item, programMetadata } = this.props;
    const { broadcastStatus, imageUrl, isLiveRecording } = this.state;
    const { recordOutcome, synopsis } = this;

    const isFuture = broadcastStatus === BroadcastStatus.Future;
    const imageStyle = imageUrl ? { backgroundImage: `url(${imageUrl})` } : {};
    const recordErrorPicto = this.renderRecordErrorPicto(recordOutcome);

    const liveRecordingElement = isLiveRecording ? (
      <div className='liveRecording'>
        <PictoRecord aria-hidden className='recording live' />
        <div className='text'>{Localizer.localize('common.status.live_recording')}</div>
      </div>
    ) : null;

    return (
      <div
        aria-hidden={!isExpanded}
        aria-labelledby={`${isFuture ? 'future' : 'available'}-episode-header-${index}`}
        aria-live={isExpanded ? 'polite' : 'off'}
        className='content'
        id={`${isFuture ? 'future' : 'available'}-episode-panel-${index}`}
        role='region'
      >
        <div className='coverWrapper'>
          <div className='cover' style={imageStyle} />
          {this.renderLockOverlay()}
        </div>
        <div className='details'>
          <div className='programInfo'>
            <StatusPicto item={item} />
            {liveRecordingElement}
            {recordErrorPicto}
            {this.renderTime()}
          </div>
          <div className='programInfo'>
            {renderProgramDetails(item, programMetadata, null, PROGRAM_INFO.Duration | PROGRAM_INFO.ReleaseDate | PROGRAM_INFO.Language | PROGRAM_INFO.ParentalGuidance)}
          </div>
          <div className='synopsis'>
            <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.synopsis')}</span>
            {synopsis}
          </div>
        </div>
      </div>
    );
  };

  render(): React.Node {
    const {
      isExpanded,
      item: {
        selectedLocation: { id },
      },
    } = this.props;

    return (
      <div className={clsx('episode', 'tv', isExpanded && 'expanded')} data-qa-location-id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxTVEpisodeReducerStateType => {
  return {
    channels: state.appConfiguration.deviceChannels,
    commercialOffers: state.appConfiguration.rightsCommercialOffers,
    defaultOnItemClick: state.ui.defaultActions ? state.ui.defaultActions.onItemClick : null,
    defaultRights: state.appConfiguration.rightsDefault,
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    isSubscriptionFeatureEnabled: state.appConfiguration.features[FEATURE_SUBSCRIPTION],
    npvrRecordingsFuture: state.npvr.npvrRecordingsFuture,
    npvrRecordingsList: state.npvr.npvrRecordingsList,
    npvrScheduledRecordingsList: state.npvr.npvrScheduledRecordingsList,
    userRights: state.appConfiguration.rightsUser,
    viewingHistory: state.ui.viewingHistory,
    viewingHistoryStatus: state.ui.viewingHistoryStatus,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxTVEpisodeDispatchToPropsType => {
  return {
    localGetImageUrl: (assetId: string, width: number, height: number, signal?: AbortSignal): Promise<string> =>
      dispatch(
        getImageUrl(
          {
            assetId,
            height,
            width,
          },
          signal,
        ),
      ),

    localHideModal: (): void => dispatch(hideModal()),

    localSendV8MetadataLocationRequest: (assetId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE> => dispatch(sendV8MetadataLocationRequest(assetId, signal)),

    localSendV8RecordingsMetadataRequest: (recordId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE> => dispatch(sendV8RecordingsMetadataRequest(recordId, signal)),

    localShowMessageModal: (messageData: MESSAGE_MODAL_TYPE): void => dispatch(showMessageModal(messageData)),

    localShowPinModal: (data: PIN_DATA_MODAL_TYPE): void => dispatch(showPinModal(data)),
  };
};

const TVEpisodeView: React.ComponentType<TVEpisodePropType> = connect(mapStateToProps, mapDispatchToProps)(TVEpisode);

export default TVEpisodeView;
